import * as React from 'react';

import { Progress } from '@longline/aqua-ui/controls/Progress';
import { TertiaryButton } from '@longline/aqua-ui/controls/TertiaryButton';

import { OrganixStatus } from './OrganixStatus';
import { useOrganixContext } from './IOrganixContext';
import { waitForForgeJob, downloadForgeResult } from './ForgeJobRunner';
import { ForgeApi } from '../api/services/ForgeApi';
import { GridExporterJSON } from '../GridExporterJSON';
import { useGlobalState } from '../contexts/state/GlobalStateContext';
import { IArea, modelCodeFor } from '../types/IArea';
import { IOrganixGridPoint } from '../types/IOrganixGridPoint';
import { calculateGrids } from '../util/GridUtils';

interface IPathogenResult {
  grid: number;
  output: any;
}

interface IPathogenFailure {
  grid: number;
  message: string;
}

interface IProps {
  area: IArea;
  data: IOrganixGridPoint[];
  onCancel: () => void;
  onError: (msg: string) => void;
  /** Called once every grid has run, if at least one succeeded. */
  onComplete: (results: IPathogenResult[], failures: IPathogenFailure[]) => void;
}

/**
 * Runs the pathogen model once per grid, one grid at a time.
 *
 * The model treats the structures it is given as the infected farm, so each
 * grid is its own job: the payload is built once, and for each grid the other
 * grids' cells have structureIndex and stocking set to null. Structures are
 * not renumbered, and metadata and structures[] still list all of them.
 *
 * Serial by design: pathogen runs are quick, and it keeps the load on AWS to
 * one task per user. A grid that fails is reported, and the grids that
 * succeeded are kept.
 */
const PathogenSimulateRemoteForge = (props: IProps) => {
  const globalState = useGlobalState();
  const { simulation } = useOrganixContext();

  const [gridNumber, setGridNumber] = React.useState<number | null>(null);
  const [gridCount, setGridCount] = React.useState<number>(0);
  const [progress, setProgress] = React.useState<number>(0);
  const cancelled = React.useRef(false);
  const currentJobID = React.useRef<string | null>(null);

  const run = async () => {
    if (!props.area.driverset) {
      props.onError("No driverset selected.");
      return;
    }
    // The snapshot, since the grid's structureIndex values were computed from it.
    const structures = simulation.structures;
    const grids = calculateGrids(structures);
    if (grids.length === 0) {
      props.onError("A pathogen simulation needs at least one structure.");
      return;
    }
    setGridCount(grids.length);

    const input = await GridExporterJSON.run(globalState.project, props.area.driverset, props.area, modelCodeFor('pathogens'), structures, props.data, globalState.species);

    const results: IPathogenResult[] = [];
    const failures: IPathogenFailure[] = [];

    for (let g = 0; g < grids.length; g++) {
      if (cancelled.current) return;
      const grid = grids[g];
      setGridNumber(grid.number);
      setProgress(g / grids.length * 100);

      // structureIndex is 1-based into the structures list.
      const inGrid = new Set(grid.structures.map(s => structures.indexOf(s) + 1));
      const gridInput = {
        ...input,
        grid: input.grid.map(p => p.structureIndex == null || inGrid.has(p.structureIndex)
          ? p
          : { ...p, structureIndex: null, stocking: null })
      };

      const jobID = await ForgeApi.start(gridInput, ["motorganix"]);
      if (cancelled.current) {
        if (jobID) ForgeApi.cancel(jobID);
        return;
      }
      if (!jobID) {
        failures.push({ grid: grid.number, message: "failed to start" });
        continue;
      }
      currentJobID.current = jobID;

      try {
        const job = await waitForForgeJob(jobID, () => cancelled.current,
          (p) => setProgress((g + p / 100) / grids.length * 100));
        if (!job) return; // cancelled
        const url = job.simulations?.find(s => s.simulation_type === "motorganix")?.download_url;
        if (!url) throw new Error("no download URL in completed job");
        results.push({ grid: grid.number, output: await downloadForgeResult(url) });
      } catch (err) {
        if (cancelled.current) return;
        failures.push({ grid: grid.number, message: err instanceof Error ? err.message : String(err) });
      } finally {
        currentJobID.current = null;
      }
    }

    if (cancelled.current) return;
    setProgress(100);
    if (results.length === 0) {
      props.onError("The pathogen simulation failed for every grid: " + failures.map(f => `Grid ${f.grid} ${f.message}`).join("; ") + ".");
      return;
    }
    props.onComplete(results, failures);
  }

  React.useEffect(() => {
    run();
    return () => { cancelled.current = true; };
  }, []);

  const handleCancel = () => {
    cancelled.current = true;
    // Not awaited: the server-side stop shouldn't hold up closing the dialog.
    if (currentJobID.current) ForgeApi.cancel(currentJobID.current);
    props.onCancel();
  }

  return (
    <OrganixStatus>
      <div style={{ marginBottom: 12 }}>
        {gridNumber === null
          ? <div>Pathogen simulation initialising.</div>
          : <div>Pathogen simulation running: grid {gridNumber} of {gridCount}.</div>}
      </div>
      <Progress width="300px" thickness={8} numbered value={progress}/>
      <TertiaryButton onClick={handleCancel}>CANCEL</TertiaryButton>
    </OrganixStatus>
  )
}

export { PathogenSimulateRemoteForge, IPathogenResult, IPathogenFailure }
